import type { SellingPlan, Variant } from "@/hooks/usePurchasedProductDetail";

/** Flatten selling plans from selling_plan_groups or legacy selling_plans */
export function getSellingPlansFromVariant(
  variant: Variant | null | undefined
): SellingPlan[] {
  if (!variant) return [];
  if (variant.selling_plan_groups?.length) {
    return variant.selling_plan_groups.flatMap((g) => g.selling_plans ?? []);
  }
  return variant.selling_plans ?? [];
}

/** Compute discounted price and save % for a selling plan */
export function getPlanPricing(
  variantPrice: number,
  plan: SellingPlan
): { discountedPrice: number; originalPrice: number; savePercent: number } {
  const originalPrice = variantPrice;
  const adj = plan.price_adjustments?.[0];
  let discountedPrice = variantPrice;
  let savePercent = 0;
  if (adj) {
    if (
      adj.adjustment_type === "PERCENTAGE" &&
      adj.adjustment_value?.percentage != null
    ) {
      discountedPrice =
        variantPrice * (1 - adj.adjustment_value.percentage / 100);
      savePercent =
        originalPrice > 0
          ? Math.round(
              ((originalPrice - discountedPrice) / originalPrice) * 100,
            )
          : 0;
    } else if (
      adj.adjustment_type === "PRICE" &&
      adj.adjustment_value?.amount != null
    ) {
      // PRICE amount is the per-delivery price (same as PERCENTAGE result).
      discountedPrice =
        parseFloat(adj.adjustment_value.amount) || variantPrice;
      savePercent =
        originalPrice > 0
          ? Math.round(
              ((originalPrice - discountedPrice) / originalPrice) * 100,
            )
          : 0;
    }
  }
  return {
    discountedPrice: Math.round(discountedPrice * 100) / 100,
    originalPrice,
    savePercent,
  };
}

const WORD_TO_NUM: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
};

function parseFrequencyNumber(s: string): number | null {
  const d = parseInt(s, 10);
  if (!isNaN(d)) return d;
  return WORD_TO_NUM[s.toLowerCase()] ?? null;
}

/** Normalize a plan label to "Monthly Delivery" or "N-Month Delivery". */
function humanizeFrequency(raw: string): string {
  const everyMatch = raw.match(/Every\s+(\w+)\s+Months?\b/i);
  if (everyMatch) {
    const n = parseFrequencyNumber(everyMatch[1]);
    if (n != null) {
      return n === 1 ? "Monthly Delivery" : `${n}-Month Delivery`;
    }
  }
  const nMonthsMatch = raw.match(/\b(\w+)\s*months?\b/i);
  if (nMonthsMatch) {
    const n = parseFrequencyNumber(nMonthsMatch[1]);
    if (n != null) {
      return n === 1 ? "Monthly Delivery" : `${n}-Month Delivery`;
    }
  }
  if (/\bmonthly\b/i.test(raw)) return "Monthly Delivery";
  return raw;
}

/** Get display label from plan - use options[0].value or plan.name */
export function getPlanDisplayName(plan: SellingPlan): string {
  const raw = plan.options?.[0]?.value ?? plan.name;
  return humanizeFrequency(raw);
}

/** Extract the number of months from a plan (defaults to 1). */
export function getPlanMonths(plan: SellingPlan): number {
  const opt = plan.options?.[0];
  const raw = opt?.value ?? plan.name;
  const everyMatch = raw.match(/Every\s+(\w+)\s+Months?\b/i);
  if (everyMatch) {
    return parseFrequencyNumber(everyMatch[1]) ?? 1;
  }
  const nMonthsMatch = raw.match(/\b(\w+)\s*months?\b/i);
  if (nMonthsMatch) {
    const n = parseFrequencyNumber(nMonthsMatch[1]);
    if (n != null) return n;
  }
  return 1;
}

/** Check if plan qualifies for "Free Shipping" (3+ months) */
export function hasFreeShipping(plan: SellingPlan): boolean {
  const opt = plan.options?.[0]?.value ?? plan.name ?? "";
  return (
    /\b(3|6|three|six)\s*months?\b/i.test(opt) ||
    /\b(3|6)-month/i.test(opt)
  );
}
